// 折线/面积图（ECharts 封装）
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { formatNumber } from '@/lib/utils'
import { WX_PALETTE } from '@/lib/chartTheme'
import { wechatTheme } from '@/lib/theme'

export interface TrendPoint {
  date: string
  value: number
}

export interface TrendSeries {
  name: string
  color: string
  data: TrendPoint[]
}

interface Props {
  data?: TrendPoint[]
  series?: TrendSeries[]
  height?: number
  area?: boolean
  smooth?: boolean
  yLabel?: string
  className?: string
}

const PALETTE = WX_PALETTE

export default function TrendChart({
  data,
  series,
  height = 280,
  area = true,
  smooth = true,
  yLabel,
  className,
}: Props) {
  const option = useMemo<EChartsOption>(() => {
    // 单系列：把 data 转为 series
    const sList: TrendSeries[] = series ?? (data ? [{ name: 'value', color: PALETTE[0]!, data }] : [])

    const xAxisData = sList[0]?.data.map((d) => d.date) ?? []

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: wechatTheme.bg.card,
        borderColor: wechatTheme.border.DEFAULT,
        borderWidth: 1,
        textStyle: { color: wechatTheme.text.primary, fontSize: 12 },
        axisPointer: { 
          type: 'cross', 
          lineStyle: { color: wechatTheme.border.strong, type: 'dashed' },
          label: {
            backgroundColor: wechatTheme.brand.DEFAULT,
            color: '#fff',
            borderRadius: 2,
            padding: [2, 6],
            fontSize: 11,
          },
        },
        valueFormatter: (v) => formatNumber(typeof v === 'number' ? v : Number(v)),
      },
      grid: { left: 50, right: 16, top: 24, bottom: 32 },
      xAxis: {
        type: 'category',
        data: xAxisData,
        boundaryGap: false,
        axisLine: { lineStyle: { color: wechatTheme.border.DEFAULT } },
        axisLabel: { color: wechatTheme.text.tertiary, fontSize: 11 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        name: yLabel,
        nameTextStyle: { color: wechatTheme.text.tertiary, fontSize: 11 },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: wechatTheme.border.light, type: 'dashed' } },
        axisLabel: {
          color: wechatTheme.text.tertiary,
          fontSize: 11,
          formatter: (v: number) => formatNumber(v),
        },
      },
      series: sList.map((s) => ({
        name: s.name,
        type: 'line',
        smooth,
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false,
        lineStyle: { width: 3, color: s.color },
        itemStyle: { color: s.color, borderColor: '#fff', borderWidth: 2 },
        areaStyle: area
          ? {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: s.color + '66' },
                  { offset: 1, color: s.color + '00' },
                ],
              },
            }
          : undefined,
        emphasis: {
          focus: 'series',
          itemStyle: { borderColor: '#fff', borderWidth: 3 },
          lineStyle: { width: 4 },
        },
        data: s.data.map((d) => d.value),
      })),
    }
  }, [data, series, area, smooth, yLabel])

  return (
    <div className={className}>
      <ReactECharts
        option={option}
        style={{ height, width: '100%' }}
        notMerge
        lazyUpdate
        opts={{ renderer: 'canvas' }}
      />
    </div>
  )
}
